import React, { useEffect, useState } from "react";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { useIsDarkMode } from "../../theme/ThemeContext";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

// Chart chrome colors that stay legible on both a white and a slate-800 card,
// so the axes/grid need no theme branch of their own.
const AXIS_TICK = { fill: "#94a3b8", fontSize: 11 };
const AXIS_STROKE = "#94a3b8";
const GRID_STROKE = "#64748b40";

const WebtrackMonitor = () => {
  const isDark = useIsDarkMode();
  const [webtrackData, setWebtrackData] = useState([]);
  const [gatewayData, setGatewayData] = useState([]);
  const [webtrackDown, setWebtrackDown] = useState([]);
  const [gatewayDown, setGatewayDown] = useState([]);

  async function fetchStatus(url, latencyKey, setData, setDown) {
    try {
      const res = await fetch(url);
      const json = await res.json();

      const formatted = json.map((item) => ({
        time: new Date(item.createdAt).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
        latency: item[latencyKey] ?? 0,
        status: item.down ? 0 : 1,
        timestamp: new Date(item.createdAt).getTime(), // use numeric timestamp for interval math
      }));

      setData(formatted);

      let intervals = [];
      let currentInterval = null;

      const GAP_THRESHOLD = 2000; // 2 seconds allowed gap to merge

      formatted.forEach((item) => {
        if (item.status === 0) {
          if (!currentInterval) {
            currentInterval = { start: item.timestamp, end: item.timestamp };
          } else {
            currentInterval.end = item.timestamp;
          }
        } else if (currentInterval) {
          intervals.push({ ...currentInterval });
          currentInterval = null;
        }
      });

      if (currentInterval) intervals.push(currentInterval);

      // Merge intervals that are very close together
      const merged = [];
      intervals.forEach((interval) => {
        if (
          merged.length > 0 &&
          interval.start <= merged[merged.length - 1].end + GAP_THRESHOLD
        ) {
          merged[merged.length - 1].end = interval.end;
        } else {
          merged.push(interval);
        }
      });

      // Convert timestamps back to Date objects for display
      setDown(
        merged.map((i) => ({
          start: new Date(i.start),
          end: new Date(i.end),
        }))
      );
    } catch (err) {
      console.error(`Error fetching ${url}:`, err);
    }
  }

  useEffect(() => {
    fetchStatus(
      "/api/orders/webtrackStatus",
      "icmpLatency",
      setWebtrackData,
      setWebtrackDown
    );
    fetchStatus(
      "/api/orders/gatewayStatus",
      "icmpLatency",
      setGatewayData,
      setGatewayDown
    );

    const interval = setInterval(() => {
      fetchStatus(
        "/api/orders/webtrackStatus",
        "icmpLatency",
        setWebtrackData,
        setWebtrackDown
      );
      fetchStatus(
        "/api/orders/gatewayStatus",
        "icmpLatency",
        setGatewayData,
        setGatewayDown
      );
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  // Recharts renders its tooltip from inline styles, not classes, so the dark
  // shades have to be handed to it explicitly.
  const tooltipProps = isDark
    ? {
        contentStyle: {
          backgroundColor: "#1e293b",
          borderColor: "#334155",
          color: "#f1f5f9",
        },
        labelStyle: { color: "#f1f5f9" },
        itemStyle: { color: "#f1f5f9" },
      }
    : {};

  const totalDown = webtrackDown.length + gatewayDown.length;

  const chart = (title, data, latencyColor, statusColor) => (
    <section className="app-card rounded-2xl border p-5">
      <h2 className="mb-4 text-lg font-semibold tracking-tight">{title}</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis dataKey="time" tick={AXIS_TICK} stroke={AXIS_STROKE} />
          <YAxis
            yAxisId="left"
            type="number"
            domain={[0, 100]}
            allowDataOverflow
            tick={AXIS_TICK}
            stroke={AXIS_STROKE}
            label={{
              value: "Latency (ms)",
              angle: -90,
              position: "insideLeft",
              fill: AXIS_STROKE,
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 1]}
            ticks={[0, 1]}
            tick={AXIS_TICK}
            stroke={AXIS_STROKE}
          />
          <Tooltip {...tooltipProps} />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="latency"
            stroke={latencyColor}
            name="Latency (ms)"
            dot={false}
          />
          <Line
            yAxisId="right"
            type="stepAfter"
            dataKey="status"
            stroke={statusColor}
            name="Status (UP=1, DOWN=0)"
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </section>
  );

  const intervalList = (label, intervals) => (
    <div>
      <p className="app-eyebrow mb-2">{label}</p>
      <ul className="space-y-1.5">
        {intervals.map((interval, idx) => (
          <li
            key={idx}
            className="flex items-center gap-2 rounded-xl bg-rose-500/10 px-3 py-2 text-sm tabular-nums text-rose-700 dark:text-rose-300"
          >
            <span className="h-2 w-2 shrink-0 rounded-full bg-rose-500" />
            {new Date(interval.start).toLocaleTimeString()} -{" "}
            {new Date(interval.end).toLocaleTimeString()}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="xl">
        <PageHeader
          icon={MonitorHeartRoundedIcon}
          tone="sky"
          eyebrow="Everyday Tools"
          title="Webtrack Monitor"
          description="Latency and up/down status for Webtrack and the gateway over the last hour. Refreshes every 15 seconds."
        />

        <div className="space-y-5">
          {chart("Webtrack Status (Last Hour)", webtrackData, "#3b82f6", "#22c55e")}
          {chart("Gateway Status (Last Hour)", gatewayData, "#9351AD", "#ef4444")}

          <section className="app-card rounded-2xl border p-5">
            <h2 className="mb-3 text-lg font-semibold tracking-tight">
              Downtime Intervals
            </h2>

            {totalDown === 0 ? (
              <p className="flex items-center gap-2 rounded-xl bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
                <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />
                No downtime recorded in the last hour.
              </p>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2">
                {webtrackDown.length > 0 &&
                  intervalList("Webtrack:", webtrackDown)}
                {gatewayDown.length > 0 &&
                  intervalList("Gateway:", gatewayDown)}
              </div>
            )}
          </section>
        </div>
      </PageShell>
    </div>
  );
};

export default WebtrackMonitor;
